import { Button, Grid } from '@material-ui/core'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createMachine, updateMachine } from '../../actions/machineActions'
import defaultMachineThumb from '../../assets/img/default-machine.png'
import machineList from '../../data/machines'
import { useInput } from '../../hooks/useInput'
import AutoCompleteSelect from '../Form/AutoCompleteSelect'
import FileUploadTable from '../Form/FileUploadTable'
import FormContainer from '../Form/FormContainer'
import MultiFilesInput from '../Form/MultiFilesInput'
import SelectInput from '../Form/SelectInput'
import TextAreaInput from '../Form/TextAreaInput'
import TextInput from '../Form/TextInput'
import ThumbnailUpload from '../Form/ThumbnailUpload'
import Loader from '../Loader/Loader'
import Message from '../Message/Message'

const AddEditMachine = ({
  setShowAddMachine,
  machineToEdit,
  setMachineToEdit,
}) => {
  const dispatch = useDispatch()
  const [thumbnail, setThumbnail] = useState('')
  const [thumbnailImage, setThumbnailImage] = useState()
  const [error, setError] = useState('')

  const {
    value: name,
    bind: bindName,
    error: nameError,
    setError: setNameError,
    setValue: setName,
  } = useInput('')
  const {
    value: serialNum,
    bind: bindSerialNum,
    setValue: setSerialNum,
  } = useInput('')
  const { value: make, bind: bindMake } = useInput('Hexagon')
  const { value: model, setValue: setModel } = useInput('')
  const {
    value: otherModelInfo,
    bind: bindOtherModelInfo,
    setValue: setOtherModelInfo,
  } = useInput('')
  const {
    value: softwareVersion,
    bind: bindSoftwareVersion,
    setValue: setSoftwareVersion,
  } = useInput('')
  const {
    value: toolChanger,
    bind: bindToolChanger,
    setValue: setToolChanger,
  } = useInput('')
  const {
    value: wristMake,
    bind: bindWristMake,
    setValue: setWristMake,
  } = useInput('')
  const {
    value: wristAngle,
    bind: bindWristAngle,
    setValue: setWristAngle,
  } = useInput('')
  const [machineFiles, setMachineFiles] = useState([])
  const [machineFilesError, setMachineFilesError] = useState('')
  const [machineFilesUploading, setMachineFilesUploading] = useState([])
  const {
    value: additionalNotes,
    bind: bindAdditionalNotes,
    setValue: setAdditionalNotes,
  } = useInput('')

  const machineCreate = useSelector((state) => state.machineCreate)
  const {
    error: machineCreateError,
    success: createMachineSuccess,
    loading: machineCreateLoading,
  } = machineCreate
  const machineUpdate = useSelector((state) => state.machineUpdate)
  const {
    error: machineUpdateError,
    success: machineUpdateSuccess,
    loading: machineUpdateLoading,
  } = machineUpdate
  const { userInfo } = useSelector((state) => state.userLogin)
  const { project } = useSelector((state) => state.projectDetails)

  useEffect(() => {
    if (createMachineSuccess || machineUpdateSuccess) {
      setShowAddMachine(false)
    }
  }, [createMachineSuccess, setShowAddMachine, dispatch, machineUpdateSuccess])

  useEffect(() => {
    if (thumbnail !== '') {
      const getThumbnail = async () => {
        try {
          setThumbnailImage(
            `${process.env.REACT_APP_API}/api/thumbnail/${thumbnail.id}`
          )
        } catch (error) {
          console.error(error)
        }
      }

      getThumbnail()
    }
  }, [setShowAddMachine, dispatch, thumbnail, thumbnailImage])

  useEffect(() => {
    if (!!machineToEdit) {
      setName(machineToEdit.name)
      setSerialNum(machineToEdit.serialNum)
      setModel(machineToEdit.model)
      setSoftwareVersion(machineToEdit.softwareVersion)
      setWristMake(machineToEdit.wristMake)
      setWristAngle(machineToEdit.wristAngle)
      setMachineFiles(machineToEdit.machineFiles)
      setAdditionalNotes(machineToEdit.additionalNotes)
      setThumbnail(machineToEdit.thumbnail)
      setOtherModelInfo(machineToEdit.otherModelInfo)
      setToolChanger(
        machineToEdit.toolChanger !== undefined
          ? machineToEdit.toolChanger === true
            ? 'Yes'
            : 'No'
          : ''
      )
    }
  }, [
    machineToEdit,
    setName,
    setSerialNum,
    setModel,
    setSoftwareVersion,
    setToolChanger,
    setWristMake,
    setWristAngle,
    setMachineFiles,
    setAdditionalNotes,
    setThumbnail,
    setOtherModelInfo,
  ])

  const createNewMachine = () => {
    if (formIsReady()) {
      const machine = {
        user: userInfo.isAdmin ? project.user : userInfo._id,
        name,
        serialNum,
        make,
        model,
        otherModelInfo,
        softwareVersion,
        toolChanger: toolChanger === 'Yes' ? true : false,
        wristMake,
        wristAngle,
        machineFiles,
        additionalNotes,
        thumbnail,
      }
      dispatch(createMachine(machine))
    }
  }

  const formIsReady = () => {
    let ready = true

    if (name === '') {
      ready = false
      setNameError('Please enter a machine name')
    } else {
      setNameError('')
    }

    if (!ready) {
      setError('Please correct all form errors above')
    } else {
      setError('')
    }

    return ready
  }

  const handleUpdateMachine = () => {
    if (formIsReady()) {
      let updatedMachine = {
        ...machineToEdit,
        name,
        serialNum,
        make,
        model,
        otherModelInfo,
        softwareVersion,
        toolChanger: toolChanger === 'Yes' ? true : false,
        wristMake,
        wristAngle,
        machineFiles,
        additionalNotes,
        thumbnail,
      }

      dispatch(updateMachine(updatedMachine))
    }
  }

  const handleCancelClick = () => {
    setShowAddMachine(false)
    setMachineToEdit('')
  }

  return (
    <div className="add-machine">
      <FormContainer>
        <h4 className="m-b-30">
          {userInfo.isAdmin
            ? !machineToEdit
              ? `Add new machine for ${project.userName}`
              : `Edit machine for ${project.userName}`
            : !machineToEdit
            ? 'Add new machine'
            : 'Edit machine'}
        </h4>
        <Grid container spacing={4}>
          <Grid item xs={12} className="p-b-0">
            <TextInput
              name="name"
              label="Machine name"
              bindValue={bindName}
              error={nameError}
            />
          </Grid>

          <Grid item xs={12} className="p-b-0">
            <TextInput
              name="serialNum"
              label="Serial number"
              bindValue={bindSerialNum}
              className="m-b-0"
            />
          </Grid>

          <Grid item sm={6} className="p-b-0">
            <SelectInput
              label="Make"
              name="make"
              options={[
                { value: 'Hexagon', label: 'Hexagon' },
                { value: 'Non-Hexagon', label: 'Non-Hexagon' },
              ]}
              bindValue={bindMake}
              disabled={true}
            />
          </Grid>
          <Grid item sm={6} className="p-b-0">
            <AutoCompleteSelect
              label="Model"
              name="model"
              options={machineList}
              value={model}
              setValue={setModel}
              minWidth={340}
            />
          </Grid>

          {model === 'Other' && (
            <>
              <Grid item sm={6} className="p-b-0">
                <TextInput
                  name="otherModelInfo"
                  label="Model name and info"
                  bindValue={bindOtherModelInfo}
                />
              </Grid>
              <Grid item sm={6}></Grid>
            </>
          )}

          <Grid item sm={6}>
            <TextInput
              name="softwareVersion"
              label="Software version"
              bindValue={bindSoftwareVersion}
            />
          </Grid>

          <Grid item sm={6}>
            <SelectInput
              label="Tool changer"
              name="toolChanger"
              options={[
                { value: 'Yes', label: 'Yes' },
                { value: 'No', label: 'No' },
              ]}
              bindValue={bindToolChanger}
            />
          </Grid>

          <Grid item sm={6} className="p-b-0 p-t-0">
            <SelectInput
              label="Wrist make"
              name="wristMake"
              options={[
                { value: 'Hexagon', label: 'Hexagon' },
                { value: 'Non-Hexagon', label: 'Non-Hexagon' },
                { value: 'No wrist/Fixed head', label: 'No wrist/Fixed head' },
              ]}
              bindValue={bindWristMake}
            />
          </Grid>

          <Grid item sm={6} className="p-b-0 p-t-0">
            <SelectInput
              label="Wrist angle"
              name="wristAngle"
              options={[
                { value: 2.5, label: '2.5 deg' },
                { value: 5, label: '5 deg' },
                { value: 7.5, label: '7.5 deg' },
              ]}
              bindValue={bindWristAngle}
            />
          </Grid>

          <Grid item sm={12} className="p-b-0">
            <MultiFilesInput
              name="partProgramFiles"
              label="Machine files"
              filesUploading={machineFilesUploading}
              setFiles={setMachineFiles}
              setFilesUploading={setMachineFilesUploading}
              contentTop={
                <>
                  Drag and drop or click to add
                  <strong> Machine files</strong>
                </>
              }
              contentBottom="Please include your probe configuration and reference files"
              error={machineFilesError}
              setError={setMachineFilesError}
            />

            {(machineFiles.length > 0 || machineFilesUploading.length > 0) && (
              <FileUploadTable
                data={machineFiles}
                setData={setMachineFiles}
                filesUploading={machineFilesUploading}
              />
            )}
          </Grid>

          <Grid item sm={6} className="p-t-0">
            <TextAreaInput
              name="additionalNotes"
              label="Additional notes"
              bindValue={bindAdditionalNotes}
              rows={4}
              className="tall"
            />
          </Grid>

          <Grid item sm={6} className="p-t-0">
            <ThumbnailUpload
              defaultImage={defaultMachineThumb}
              setThumbnail={setThumbnail}
              setThumbnailImage={setThumbnailImage}
              thumbnailImage={thumbnailImage}
            />
          </Grid>
        </Grid>

        <div className="m-t-40 flex flex-end">
          <div>
            <Button
              variant="outlined"
              color="primary"
              onClick={handleCancelClick}
              disabled={machineCreateLoading || machineUpdateLoading}
            >
              Cancel
            </Button>

            {machineToEdit ? (
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 20 }}
                onClick={handleUpdateMachine}
                disabled={machineCreateLoading || machineUpdateLoading}
                className={
                  machineCreateLoading || machineUpdateLoading ? 'loading' : ''
                }
              >
                {machineCreateLoading || machineUpdateLoading ? (
                  <Loader />
                ) : (
                  'Update machine'
                )}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                style={{ marginLeft: 20 }}
                onClick={createNewMachine}
                disabled={machineCreateLoading || machineUpdateLoading}
                className={
                  machineCreateLoading || machineUpdateLoading ? 'loading' : ''
                }
              >
                {machineCreateLoading || machineUpdateLoading ? (
                  <Loader />
                ) : (
                  'Add machine'
                )}
              </Button>
            )}
          </div>
        </div>

        {error && <Message variant="error">{error}</Message>}

        {machineCreateError && (
          <Message variant="error">{machineCreateError}</Message>
        )}

        {machineUpdateError && (
          <Message variant="error">{machineUpdateError}</Message>
        )}
      </FormContainer>
    </div>
  )
}

export default AddEditMachine
